import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import {
  ArrowRight, CheckCircle2, Mail, Boxes, Database, Snowflake, Globe,
  BookOpen, Sparkles, Layers, FileCheck2, Quote,
} from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { PageHero } from "../components/PageHero";
import { Reveal } from "../components/Reveal";
import { Button } from "../components/ui/button";
import { PARTNERS, CLIENTS } from "../mock";
import { LogoImage } from "../components/LogoImage";
import { PRODUCTS_DATA } from "../data/productsData";
import { Eyebrow } from "../components/Eyebrow";

const ACCENTS = [
  "from-[#15348C] to-[#0A1F5C]",
  "from-[#6BBE32] to-[#4F9622]",
  "from-[#8BD835] to-[#6BBE32]",
];

const TECH_ICON_MAP = {
  "AWS": Globe,
  "Microsoft Azure": Globe,
  "Google Cloud": Globe,
  "Kubernetes": Boxes,
  "Databricks": Database,
  "Snowflake": Snowflake,
};

const EXPLORE_ICONS = [Layers, Sparkles, BookOpen, FileCheck2];

const ProductPage = () => {
  const { slug } = useParams();
  const data = PRODUCTS_DATA[slug];

  if (!data) return <Navigate to="/offerings/all" replace />;

  const {
    eyebrow, title, subtitle, highlights,
    stats,
    differenceEyebrow, differenceHeading, differenceCopy, differenceCtaLabel, differenceCtaHref,
    whyMattersHeading, whyMatters,
    whatHeading, whatCards,
    capabilitiesHeading, capabilitiesIntro, capabilities, capabilitiesCtaLabel, capabilitiesCtaHref,
    howHeading, howSteps,
    outcomesHeading, outcomes,
    techEcosystemHeading, techEcosystem,
    whoEyebrow, whoHeading, whoItems,
    testimonial,
    ctaHeadline, ctaSupporting,
    exploreHeading, exploreCards,
  } = data;

  return (
    <div className="bg-white min-h-screen">
      <Header />

      {/* 1) HERO */}
      <PageHero eyebrow={eyebrow} title={title} subtitle={subtitle} highlights={highlights} />

      {/* Floating CTA card, overlapping the hero */}
      <section className="relative z-10 -mt-8 max-w-3xl mx-auto px-4">
        <Reveal variant="scale">
          <div className="relative">
            <div className="pointer-events-none absolute -inset-3 bg-gradient-to-r from-[#15348C]/10 via-[#6BBE32]/10 to-[#8BD835]/10 rounded-3xl blur-xl" />
            <div className="relative bg-white rounded-2xl shadow-2xl shadow-[#15348C]/15 border border-gray-100 overflow-hidden">
              <div className="h-1 bg-gradient-to-r from-[#15348C] via-[#6BBE32] to-[#8BD835]" />
              <div className="px-6 py-6 flex flex-wrap gap-3 justify-center items-center">
                <Link to="/contact">
                  <Button className="sg-btn-glow bg-[#15348C] hover:bg-[#0A1F5C] text-white font-semibold px-6 h-12 rounded-md shadow-lg shadow-[#15348C]/25">
                    Talk to Our Expert <ArrowRight size={17} className="ml-1" />
                  </Button>
                </Link>
                <Link to="/demo">
                  <Button className="sg-btn-glow bg-white text-[#15348C] border-2 border-[#15348C] hover:bg-[#15348C] hover:text-white font-semibold px-6 h-12 rounded-md">
                    Book a Demo
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </Reveal>
      </section>

      {/* 1b) TRUST / STATS STRIP (optional) */}
      {stats && stats.length > 0 && (
        <section className="pt-14 pb-4">
          <div className="max-w-6xl mx-auto px-4">
            <Reveal>
              <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-gray-100 rounded-2xl border border-gray-100 bg-white shadow-sm">
                {stats.map((s) => (
                  <div key={s.label} className="p-6 text-center group">
                    <div className="text-3xl lg:text-4xl font-bold text-[#15348C] group-hover:text-[#6BBE32] transition-colors">{s.value}</div>
                    <div className="text-sm text-gray-500 mt-1">{s.label}</div>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
        </section>
      )}

      {/* 1c) THE DIFFERENCE (optional editorial section, no cards) */}
      {differenceHeading && (
        <section className="py-20 bg-white">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <Reveal>
              <div className="flex justify-center"><Eyebrow color="#6BBE32">{differenceEyebrow || "THE DIFFERENCE"}</Eyebrow></div>
              <h2 className="text-[#15348C] leading-tight">{differenceHeading}</h2>
              {differenceCopy && <p className="mt-5 text-gray-600 text-lg leading-relaxed">{differenceCopy}</p>}
              {differenceCtaLabel && (
                <Link to={differenceCtaHref || "/contact"} className="mt-6 inline-flex items-center gap-1.5 text-[#15348C] font-semibold hover:text-[#6BBE32] transition-colors">
                  {differenceCtaLabel} <ArrowRight size={16} />
                </Link>
              )}
            </Reveal>
          </div>
        </section>
      )}

      {/* 2) WHY IT MATTERS (optional) */}
      {whyMatters && whyMatters.length > 0 && (
        <section className="pt-16 pb-20 bg-[#F8F9FA]">
          <div className="max-w-7xl mx-auto px-4">
            <Reveal>
              <div className="max-w-3xl">
                <Eyebrow color="#4F9622">WHY IT MATTERS</Eyebrow>
                <h2 className="text-[#15348C] leading-tight">{whyMattersHeading || "The gaps teams face without it."}</h2>
              </div>
            </Reveal>
            <div className="mt-10 grid md:grid-cols-3 gap-6">
              {whyMatters.map((w, i) => (
                <Reveal key={w} variant="up" delay={i * 90}>
                  <div className="group relative bg-white border border-gray-100 rounded-2xl p-7 h-full overflow-hidden hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
                    <div className="absolute top-0 left-0 w-1.5 h-full bg-gradient-to-b from-[#6BBE32] to-[#4F9622]" />
                    <span className="absolute -top-3 -right-1 text-7xl font-extrabold text-gray-50 select-none leading-none">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <div className="relative">
                      <div className="w-9 h-9 rounded-lg bg-[#4F9622] text-white flex items-center justify-center font-bold text-sm mb-5 shadow-md shadow-[#4F9622]/30">
                        {i + 1}
                      </div>
                      <p className="text-gray-700 leading-relaxed">{w}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* 3) WHAT IT IS (optional) */}
      {whatCards && whatCards.length > 0 && (
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4">
            <Reveal>
              <div className="max-w-3xl">
                <Eyebrow color="#6BBE32">WHAT IT IS</Eyebrow>
                <h2 className="text-[#15348C] leading-tight">{whatHeading || "Built around three connected pillars."}</h2>
              </div>
            </Reveal>

            <div className="mt-10 grid md:grid-cols-3 gap-6">
              {whatCards.map((c, i) => {
                const Icon = c.icon;
                return (
                  <Reveal key={c.title} variant="up" delay={i * 100}>
                    <div className="group relative bg-white border border-gray-100 rounded-2xl p-7 h-full overflow-hidden hover:border-[#6BBE32]/40 hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
                      <span className="absolute -bottom-4 -right-2 text-8xl font-extrabold text-gray-50 select-none leading-none">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <div className="relative">
                        <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#6BBE32] to-[#4F9622] flex items-center justify-center shadow-lg shadow-[#6BBE32]/30 mb-5 group-hover:scale-110 transition-transform duration-300">
                          <Icon size={26} className="text-white" />
                        </div>
                        <h3 className="text-[#15348C] group-hover:text-[#6BBE32] transition-colors" style={{ fontSize: "1.35rem" }}>{c.title}</h3>
                        <p className="mt-3 text-gray-600 leading-relaxed">{c.desc}</p>
                      </div>
                    </div>
                  </Reveal>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* 4) KEY CAPABILITIES */}
      <section className="py-20 bg-[#F8F9FA]">
        <div className="max-w-7xl mx-auto px-4">
          <Reveal>
            <div className="max-w-3xl mb-12">
              <Eyebrow color="#4F9622">KEY CAPABILITIES</Eyebrow>
              <h2 className="text-[#15348C] leading-tight">{capabilitiesHeading || "Everything built in, from day one."}</h2>
              {capabilitiesIntro && <p className="mt-3 text-gray-600 leading-relaxed">{capabilitiesIntro}</p>}
            </div>
          </Reveal>

          <div className={`grid sm:grid-cols-2 gap-6 ${capabilities.length > 4 ? "lg:grid-cols-3" : "lg:grid-cols-4"}`}>
            {capabilities.map((c, i) => {
              const Icon = c.icon;
              const accent = ACCENTS[i % ACCENTS.length];
              return (
                <Reveal key={c.title} variant="up" delay={i * 80}>
                  <div className="group bg-white border border-gray-100 rounded-2xl p-6 h-full hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
                    <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${accent} flex items-center justify-center mb-4 shadow-md group-hover:scale-110 transition-transform duration-300`}>
                      <Icon size={20} className="text-white" />
                    </div>
                    <h3 className="text-[#15348C]" style={{ fontSize: "1.05rem" }}>{c.title}</h3>
                    <p className="mt-2 text-gray-600 text-sm leading-relaxed">{c.desc}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>

          {capabilitiesCtaLabel && (
            <Reveal delay={120}>
              <div className="mt-10 text-center">
                <Link to={capabilitiesCtaHref || "/offerings/all"} className="inline-flex items-center gap-1.5 text-[#15348C] font-semibold hover:text-[#6BBE32] transition-colors">
                  {capabilitiesCtaLabel} <ArrowRight size={16} />
                </Link>
              </div>
            </Reveal>
          )}
        </div>
      </section>

      {/* 5) HOW IT WORKS */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <Reveal>
            <div className="max-w-3xl mb-14">
              <Eyebrow color="#6BBE32">HOW IT WORKS</Eyebrow>
              <h2 className="text-[#15348C] leading-tight">{howHeading || "From first launch to outcomes, in four steps."}</h2>
            </div>
          </Reveal>

          <div className="relative">
            <div
              className="hidden lg:block absolute top-5 h-0.5 bg-gradient-to-r from-[#15348C] via-[#6BBE32] to-[#8BD835]"
              style={{ left: "12.5%", right: "12.5%" }}
            />
            <div className="relative grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {howSteps.map((step, i) => {
                const stepTitle = typeof step === "string" ? null : step.title;
                const stepDesc = typeof step === "string" ? step : step.desc;
                return (
                  <Reveal key={stepTitle || stepDesc} variant="up" delay={i * 90}>
                    <div className="relative bg-white border border-gray-100 rounded-2xl p-6 pt-8 h-full hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
                      <div className="absolute -top-5 left-6 w-10 h-10 rounded-full bg-gradient-to-br from-[#15348C] to-[#0A1F5C] text-white flex items-center justify-center font-bold shadow-lg shadow-[#15348C]/30 ring-4 ring-white">
                        {i + 1}
                      </div>
                      {stepTitle && <h3 className="mt-3 text-[#15348C]" style={{ fontSize: "1.05rem" }}>{stepTitle}</h3>}
                      <p className={`text-gray-600 leading-relaxed text-sm ${stepTitle ? "mt-2" : "mt-3"}`}>{stepDesc}</p>
                    </div>
                  </Reveal>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* 5b) BUSINESS OUTCOMES (optional) */}
      {outcomes && outcomes.length > 0 && (
        <section className="py-20 bg-gradient-to-br from-[#F8F9FA] to-[#EFF3E8]">
          <div className="max-w-7xl mx-auto px-4">
            <Reveal>
              <div className="max-w-3xl mb-10">
                <Eyebrow color="#4F9622">BUSINESS OUTCOMES</Eyebrow>
                <h2 className="text-[#15348C] leading-tight">{outcomesHeading || "Outcomes that matter."}</h2>
              </div>
            </Reveal>
            <div className="grid md:grid-cols-3 gap-6">
              {outcomes.map((o, i) => (
                <Reveal key={o} variant="up" delay={i * 90}>
                  <div className="group flex gap-4 bg-white border border-gray-100 rounded-2xl p-6 h-full hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
                    <div className="w-9 h-9 rounded-full bg-[#8BD835]/15 flex items-center justify-center shrink-0">
                      <CheckCircle2 size={20} className="text-[#4F9622]" />
                    </div>
                    <p className="text-gray-700 leading-relaxed">{o}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* 5c) TECHNOLOGY ECOSYSTEM (optional) */}
      {techEcosystem && techEcosystem.length > 0 && (
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 text-center">
            <Reveal>
              <div className="flex justify-center">
                <Eyebrow color="#6BBE32">TRUSTED TECHNOLOGY ECOSYSTEM</Eyebrow>
              </div>
              <h2 className="text-[#15348C] leading-tight max-w-2xl mx-auto">{techEcosystemHeading || "Practical learning across leading technology platforms."}</h2>
              <div className="mt-9 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
                {techEcosystem.map((t) => {
                  const Icon = TECH_ICON_MAP[t] || Globe;
                  return (
                    <div key={t} className="group flex flex-col items-center gap-3 bg-[#F8F9FA] border border-gray-100 rounded-2xl px-4 py-6 hover:bg-white hover:shadow-lg hover:-translate-y-1 transition-all duration-300">
                      <div className="w-11 h-11 rounded-xl bg-[#15348C]/10 flex items-center justify-center group-hover:bg-[#15348C] transition-colors">
                        <Icon size={20} className="text-[#15348C] group-hover:text-white transition-colors" />
                      </div>
                      <span className="text-sm font-semibold text-[#15348C]">{t}</span>
                    </div>
                  );
                })}
              </div>
            </Reveal>
          </div>
        </section>
      )}

      {/* 6) PROOF STRIP */}
      <section className="py-16 bg-[#F8F9FA] border-y border-gray-100">
        <div className="max-w-7xl mx-auto px-4">
          <Reveal>
            <div className="text-center text-xs font-bold tracking-widest text-gray-500 mb-6">TRUSTED BY GLOBAL ENTERPRISES</div>
            <div
              className="flex flex-wrap items-center justify-center gap-x-10 gap-y-5 opacity-80"
              style={{ maskImage: "linear-gradient(to right, transparent, black 8%, black 92%, transparent)", WebkitMaskImage: "linear-gradient(to right, transparent, black 8%, black 92%, transparent)" }}
            >
              {PARTNERS.slice(0, 10).map((p) => (
                <LogoImage key={p.name} src={p.logo} alt={p.name}
                  className="h-8 max-w-[120px] object-contain"
                  fallbackClassName="text-[#15348C] font-bold text-sm" />
              ))}
            </div>
            {CLIENTS && CLIENTS.length > 0 && (
              <div
                className="mt-8 flex flex-wrap items-center justify-center gap-x-10 gap-y-4 opacity-70"
                style={{ maskImage: "linear-gradient(to right, transparent, black 8%, black 92%, transparent)", WebkitMaskImage: "linear-gradient(to right, transparent, black 8%, black 92%, transparent)" }}
              >
                {CLIENTS.slice(0, 8).map((c) => (
                  <LogoImage key={c.name} src={c.logo} alt={c.name}
                    className="h-7 max-w-[110px] object-contain"
                    fallbackClassName="text-gray-500 font-semibold text-sm" />
                ))}
              </div>
            )}
          </Reveal>
        </div>
      </section>

      {/* 7) WHO IT'S FOR (optional; reused as "USE CASES" etc. via whoEyebrow) */}
      {whoItems && whoItems.length > 0 && (
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4">
            <Reveal>
              <div className="max-w-3xl mb-10">
                <Eyebrow color="#4F9622">{whoEyebrow || "WHO IT'S FOR"}</Eyebrow>
                <h2 className="text-[#15348C] leading-tight">{whoHeading || "Built for the teams that need it most."}</h2>
              </div>
            </Reveal>
            <div className={`grid gap-6 ${whoItems.length > 3 ? "md:grid-cols-2 lg:grid-cols-4" : "md:grid-cols-3"}`}>
              {whoItems.map((w, i) => {
                const whoTitle = typeof w === "string" ? null : w.title;
                const whoDesc = typeof w === "string" ? w : w.desc;
                return (
                  <Reveal key={whoTitle || whoDesc} variant="up" delay={i * 90}>
                    <div className="group relative flex gap-4 bg-white border border-gray-100 rounded-2xl p-6 h-full overflow-hidden hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
                      <div className="absolute top-0 left-0 w-1.5 h-full bg-gradient-to-b from-[#15348C] to-[#0A1F5C]" />
                      <CheckCircle2 size={22} className="text-[#6BBE32] shrink-0 mt-0.5 relative" />
                      <div className="relative">
                        {whoTitle && <h3 className="text-[#15348C] mb-1" style={{ fontSize: "1.05rem" }}>{whoTitle}</h3>}
                        <p className="text-gray-700 leading-relaxed">{whoDesc}</p>
                      </div>
                    </div>
                  </Reveal>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* 7b) TESTIMONIAL (optional) */}
      {testimonial && testimonial.quote && (
        <section className="py-20 bg-gradient-to-br from-[#15348C] to-[#0A1F5C] text-white relative overflow-hidden">
          <div className="absolute -top-20 right-10 w-72 h-72 bg-[#6BBE32]/15 rounded-full blur-3xl" />
          <div className="absolute -bottom-20 left-10 w-72 h-72 bg-[#8BD835]/15 rounded-full blur-3xl" />
          <div className="relative max-w-3xl mx-auto px-4 text-center">
            <Reveal>
              <Quote size={36} className="text-[#8BD835] mx-auto mb-6" />
              <p className="text-xl lg:text-2xl leading-relaxed font-medium">&ldquo;{testimonial.quote}&rdquo;</p>
              {testimonial.attribution && <p className="mt-6 text-gray-300 font-semibold">{testimonial.attribution}</p>}
            </Reveal>
          </div>
        </section>
      )}

      {/* 8) CTA BAND */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4">
          <Reveal>
            <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#15348C] to-[#0A1F5C] px-8 py-12 lg:px-14 lg:py-16 text-white">
              <div
                className="absolute inset-0 opacity-[0.06]"
                style={{
                  backgroundImage: "radial-gradient(circle, #ffffff 1px, transparent 1px)",
                  backgroundSize: "20px 20px",
                }}
              />
              <div className="absolute -top-24 -right-24 w-80 h-80 bg-[#6BBE32]/20 rounded-full blur-3xl" />
              <div className="absolute -bottom-24 -left-24 w-80 h-80 bg-[#8BD835]/20 rounded-full blur-3xl" />
              <div className="relative grid lg:grid-cols-2 gap-8 items-center">
                <div>
                  <h2 className="text-white leading-tight">{ctaHeadline}</h2>
                  {ctaSupporting && <p className="mt-3 text-gray-300">{ctaSupporting}</p>}
                </div>
                <div className="flex flex-wrap gap-4 lg:justify-end items-center">
                  <Link to="/contact">
                    <Button className="bg-[#8BD835] hover:bg-[#6BBE32] text-white font-semibold px-7 h-12 rounded-md shadow-lg shadow-[#8BD835]/30">
                      Talk to Our Expert <ArrowRight size={17} className="ml-1" />
                    </Button>
                  </Link>
                  <a href="mailto:info@skillsgain.com" className="inline-flex items-center gap-2 text-white/90 hover:text-[#8BD835] font-semibold transition-colors">
                    <Mail size={18} /> info@skillsgain.com
                  </a>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* 9) CONTINUE EXPLORING (optional) */}
      {exploreCards && exploreCards.length > 0 && (
        <section className="py-20 bg-[#F8F9FA]">
          <div className="max-w-7xl mx-auto px-4">
            <Reveal>
              <div className="max-w-3xl mb-10">
                <Eyebrow color="#6BBE32">CONTINUE EXPLORING</Eyebrow>
                <h2 className="text-[#15348C] leading-tight">{exploreHeading || "Explore More"}</h2>
              </div>
            </Reveal>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {exploreCards.map((c, i) => {
                const Icon = EXPLORE_ICONS[i % EXPLORE_ICONS.length];
                const accent = ACCENTS[i % ACCENTS.length];
                return (
                  <Reveal key={c.title} variant="up" delay={i * 90}>
                    <Link to={c.href} className="group relative flex flex-col bg-white border border-gray-100 rounded-2xl p-6 h-full overflow-hidden hover:border-[#6BBE32]/40 hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
                      <div className={`absolute top-0 left-0 right-0 h-1 bg-gradient-to-r ${accent}`} />
                      <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${accent} flex items-center justify-center mb-4 shadow-md group-hover:scale-110 transition-transform duration-300`}>
                        <Icon size={20} className="text-white" />
                      </div>
                      <h3 className="text-[#15348C] group-hover:text-[#6BBE32] transition-colors" style={{ fontSize: "1.05rem" }}>{c.title}</h3>
                      <p className="mt-2 text-gray-600 text-sm leading-relaxed flex-1">{c.desc}</p>
                      <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-[#15348C] group-hover:text-[#6BBE32] transition-colors">
                        Learn More <ArrowRight size={15} className="group-hover:translate-x-1 transition-transform" />
                      </span>
                    </Link>
                  </Reveal>
                );
              })}
            </div>
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
};

export default ProductPage;
